import { useState } from "react";
import { C, PageHeader, GlassCard, Badge, Skeleton, ProgressBar, Stat } from "@coremonit/design-system";
import { HeartPulse, Target, Gauge, Sparkles } from "lucide-react";
import { useServiceHealth, useServices } from "../api/hooks";

export function HealthPage() {
  const services = useServices();
  const [serviceId, setServiceId] = useState<string | undefined>();
  const h = useServiceHealth(serviceId);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PageHeader icon={<HeartPulse size={16} />} title="Service Health" subtitle="Availability · SLO / SLA / SLI · error budget · AI health score" />
      <GlassCard style={{ padding: 12 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ color: C.textMuted, fontSize: 11 }}>Service</span>
          <select value={serviceId ?? ""} onChange={(e) => setServiceId(e.target.value || undefined)}
            style={{ background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, padding: "6px 10px", fontSize: 12, minWidth: 260 }}>
            <option value="">Select a service…</option>
            {services.data?.items.map((s) => <option key={s.id} value={s.id} style={{ background: C.surface }}>{s.name}</option>)}
          </select>
        </div>
      </GlassCard>
      {!serviceId ? (
        <GlassCard style={{ padding: 40, textAlign: "center", color: C.textMuted, fontSize: 12 }}>Pick a service.</GlassCard>
      ) : h.isLoading ? <Skeleton height={320} /> : h.data && (
        <>
          <GlassCard glow style={{ padding: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{
                width: 92, height: 92, borderRadius: "50%",
                background: `conic-gradient(${scoreColor(h.data.aiScore)} ${h.data.aiScore * 3.6}deg, ${C.surfaceMid} 0deg)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: `0 0 30px ${scoreColor(h.data.aiScore)}44`,
              }}>
                <div style={{ width: 74, height: 74, borderRadius: "50%", background: C.surface, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ color: C.text, fontSize: 22, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{h.data.aiScore}</div>
                  <div style={{ color: C.textMuted, fontSize: 8, letterSpacing: 0.4, textTransform: "uppercase", fontWeight: 700 }}>AI Score</div>
                </div>
              </div>
              <div style={{ flex: 1, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
                <ScoreTile label="Availability" score={h.data.availabilityPct} suffix="%" color={C.green} />
                <ScoreTile label="Latency"      score={h.data.latencyScore}     color={C.blue} />
                <ScoreTile label="Errors"       score={h.data.errorScore}       color={C.amber} />
                <ScoreTile label="Traffic"      score={h.data.trafficScore}     color={C.cyan} />
                <ScoreTile label="Saturation"   score={h.data.saturationScore}  color={C.purple} />
                <ScoreTile label="Deployment"   score={h.data.deploymentScore}  color={C.pink} />
                <ScoreTile label="Dependency"   score={h.data.dependencyScore}  color={C.blue} />
              </div>
            </div>
          </GlassCard>

          <GlassCard style={{ padding: 16 }}>
            <div style={{ color: C.textSub, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 12 }}>SLOs / SLIs</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {h.data.slos.map((s) => {
                const meeting = s.currentPct >= s.targetPct;
                return (
                  <div key={s.id} style={{ padding: 12, background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, color: C.text, fontSize: 12, fontWeight: 700 }}><Target size={12} />{s.name}</div>
                      <Badge color={meeting ? "green" : "red"}>{meeting ? "meeting" : "burning"}</Badge>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, fontSize: 10, color: C.textMuted, marginBottom: 6 }}>
                      <span>Target: <span style={{ color: C.text }}>{s.targetPct}%</span></span>
                      <span>Current: <span style={{ color: meeting ? C.green : C.red }}>{s.currentPct.toFixed(2)}%</span></span>
                      <span>Budget: <span style={{ color: s.errorBudgetPct > 0 ? C.green : C.red }}>{s.errorBudgetPct.toFixed(2)}%</span></span>
                      <span>Window: <span style={{ color: C.text }}>{s.window}</span></span>
                    </div>
                    <ProgressBar value={Math.min(100, s.currentPct)} color={meeting ? C.green : C.red} />
                  </div>
                );
              })}
            </div>
          </GlassCard>
        </>
      )}
    </div>
  );
}

function ScoreTile({ label, score, color, suffix }: { label: string; score: number; color: string; suffix?: string }) {
  return (
    <div style={{ background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 12, padding: 10 }}>
      <div style={{ color: C.textMuted, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700 }}>{label}</div>
      <div style={{ color, fontSize: 15, fontWeight: 700, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>{typeof score === "number" ? score.toFixed(score < 10 ? 2 : 0) : score}{suffix}</div>
    </div>
  );
}

function scoreColor(s: number) { return s > 80 ? C.green : s > 60 ? C.amber : C.red; }
